import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class FreeShippingBar extends HTMLElement {
  get #threshold() {
    return Number(this.getAttribute('threshold') ?? 0);
  }

  get #totalPrice() {
    return Number(this.getAttribute('total-price') ?? 0);
  }

  set #totalPrice(value) {
    this.setAttribute('total-price', String(value));
  }

  get #remainingMessage() {
    return this.getAttribute('remaining-message') ?? '';
  }

  get #reachedMessage() {
    return this.getAttribute('reached-message') ?? '';
  }

  get #messageElement() {
    return this.querySelector('[data-ref="message"]');
  }

  get #progressElement() {
    return this.querySelector('[data-ref="progress"]');
  }

  connectedCallback() {
    this.#render();
    document.addEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartAdd = (event) => {
    const lineItem = event.detail?.lineItem;

    if (!lineItem?.requires_shipping) return;

    this.#totalPrice = this.#totalPrice + lineItem.final_line_price;
    this.#render();
  };

  #onCartUpdate = (event) => {
    const cart = event.detail?.resource;

    if (!cart?.items) return;

    this.#totalPrice = cart.items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);

    this.#render();
  };

  #render() {
    const messageElement = this.#messageElement;
    const progressElement = this.#progressElement;

    if (!messageElement || !progressElement) return;

    const threshold = this.#threshold;
    const totalPrice = this.#totalPrice;
    const progress = threshold > 0 ? Math.min(totalPrice / threshold, 1) : 1;

    progressElement.style.setProperty('--progress', String(progress));
    progressElement.setAttribute('aria-valuenow', String(Math.round(progress * 100)));

    if (totalPrice >= threshold) {
      messageElement.innerHTML = this.#reachedMessage;
      return;
    }

    const remaining = formatMoney(threshold - totalPrice, Theme.settings.currency_format);

    messageElement.innerHTML = this.#remainingMessage.replace(/\{\{\s*amount\s*\}\}/, remaining);
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
